import React, { useEffect, useState } from "react"
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    Button,
    Typography,
    CircularProgress,
    Box,
    Chip,
    FormControl,
    InputLabel,
    Select,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    Stack,
    FormGroup,
    FormControlLabel,
    Checkbox
} from "@mui/material"
import {
    fetchLeaveReports,
    exportLeaveReports,
} from "../../Actions/commonController"
import PaginationTable from "../../Components/Pagination"
import ResponsiveDatePicker from "../../Components/DatePicker/ResponsiveDatePicker"
import { FormatDate } from "../../Components/Utilities/DateFormatter"
import FilterListIcon from "@mui/icons-material/FilterList"
import SearchIcon from "@mui/icons-material/Search"
import DownloadIcon from "@mui/icons-material/Download"
import { toast } from "react-toastify"
import { useSearchParams } from 'react-router-dom'
import SearchBar from "../../Components/SearchBar"
import CircleIcon from "@mui/icons-material/Circle"
import CloseIcon from "@mui/icons-material/Close"
import { IconButton } from "@mui/material"
import moment from "moment"

//Field options for filters - UPDATED WITH NEW FIELDS
const fieldOptions = [
    { id: "empid", label: "EmpID" },
    { id: "employee_name", label: "Employee Name" },
    { id: "department", label: "Department" },
    { id: "dateofjoin", label: "Date of Join" },
    { id: "probation_period", label: "Probation Period" },
    { id: "type", label: "Leave Type" },
    { id: "startdate", label: "Start Date" },
    { id: "enddate", label: "End Date" },
    { id: "duration", label: "Duration" },
    { id: "status", label: "Status" },
    { id: "createdAt", label: "Request Date" },
    { id: "annual_balance", label: "Annual Leave" },
    { id: "sick_balance", label: "Sick Leave" },
    { id: "maternity_balance", label: "Maternity Leave" },
    { id: "paternity_balance", label: "Paternity Leave" },
    { id: "emergency_balance", label: "Emergency Leave" }
]

//Table columns - UPDATED WITH SEPARATE LEAVE TYPE COLUMNS
const columns = [
    { id: "id", label: "SR NO." },
    { id: "empid", label: "EMP ID" },
    { id: "employee_name", label: "EMPLOYEE NAME" },
    { id: "department", label: "DEPARTMENT" },
    { id: "dateofjoin", label: "DATE OF JOIN" },
    { id: "probation_period", label: "PROBATION PERIOD" },
    { id: "type", label: "LEAVE TYPE" },
    { id: "startdate", label: "START DATE" },
    { id: "enddate", label: "END DATE" },
    { id: "duration", label: "DURATION" },
    { id: "annual_balance", label: "ANNUAL" },
    { id: "sick_balance", label: "SICK" },
    { id: "maternity_balance", label: "MATERNITY" },
    { id: "paternity_balance", label: "PATERNITY" },
    { id: "emergency_balance", label: "EMERGENCY" },
    { id: "status", label: "STATUS" },
    { id: "createdAt", label: "REQUEST DATE" }
];

// Function to calculate probation period
const calculateProbationPeriod = (dateOfJoin) => {
    if (!dateOfJoin) return 'N/A';

    try {
        // Parse DD/MM/YYYY format
        const [day, month, year] = dateOfJoin.split('/').map(Number);
        const joinDate = new Date(year, month - 1, day);
        const today = new Date();

        if (isNaN(joinDate.getTime())) return 'Invalid Date';

        let years = today.getFullYear() - joinDate.getFullYear();
        let months = today.getMonth() - joinDate.getMonth();
        let days = today.getDate() - joinDate.getDate();

        // Adjust for negative days
        if (days < 0) {
            months--;
            // Get days in previous month
            const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            days += prevMonth.getDate();
        }

        // Adjust for negative months
        if (months < 0) {
            years--;
            months += 12;
        }

        return `${years}y ${months}m ${days}d`;
    } catch (error) {
        console.error('Error calculating probation period:', error);
        return 'Error';
    }
};

const calculateLeaveEntitlements = (dateOfJoin, leaveBalances, gender = 'unknown') => {
    if (!dateOfJoin || !leaveBalances) {
        return {
            annual: { display: '0/0' },
            sick: { display: '0/0' },
            maternity: { display: '-' },
            paternity: { display: '-' },
            emergency: { display: '0/0' }
        };
    }

    try {
        const joinDate = moment(dateOfJoin, 'DD/MM/YYYY');
        const today = moment();

        if (!joinDate.isValid()) {
            return {
                annual: { display: '0/0' },
                sick: { display: '0/0' },
                maternity: { display: '-' },
                paternity: { display: '-' },
                emergency: { display: '0/0' }
            };
        }

        // CORRECTED: Calculate complete years of service (floor the decimal years)
        const yearsOfService = Math.floor(today.diff(joinDate, 'years', true));

        // CORRECTED: Use the same logic as backend
        // Annual Leave: 30 base + (complete_years × 30)
        const annualTotal = 30 + (yearsOfService * 30);

        // Sick Leave: 90 base + (complete_years × 90)
        const sickTotal = 90 + (yearsOfService * 90);

        // Maternity Leave: Only for females
        const isFemale = gender && gender.toLowerCase() === 'female';
        const maternityTotal = isFemale ? 60 : 0;

        // Paternity Leave: Only for males
        const isMale = gender && gender.toLowerCase() === 'male';
        const paternityTotal = isMale ? 5 : 0;

        // Emergency leave: Show actual value (no fixed total)
        const emergencyTotal = leaveBalances.emergency?.total || 0;

        // Get the actual leave balances from backend
        const annualActual = leaveBalances.annual?.remaining || 0;
        const sickActual = leaveBalances.sick?.remaining || 0;
        const maternityActual = leaveBalances.maternity?.remaining || 0;
        const paternityActual = leaveBalances.paternity?.remaining || 0;
        const emergencyActual = leaveBalances.emergency?.remaining || 0;

        // Calculate display values
        const annualDisplay = `${annualActual} / ${annualTotal}`;
        const sickDisplay = `${sickActual} / ${sickTotal}`;
        const emergencyDisplay = `${emergencyActual} / ${emergencyTotal}`;

        // Maternity and paternity show "-" if not eligible
        const maternityDisplay = isFemale
            ? `${maternityActual} / ${maternityTotal}`
            : '-';

        const paternityDisplay = isMale
            ? `${paternityActual} / ${paternityTotal}`
            : '-';

        return {
            annual: { display: annualDisplay },
            sick: { display: sickDisplay },
            maternity: { display: maternityDisplay },
            paternity: { display: paternityDisplay },
            emergency: { display: emergencyDisplay }
        };
    } catch (error) {
        console.error('Error calculating leave entitlements:', error);
        return {
            annual: { display: '0/0' },
            sick: { display: '0/0' },
            maternity: { display: '-' },
            paternity: { display: '-' },
            emergency: { display: '0/0' }
        };
    }
};

export default function LeaveReportsTable() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [todayDate, setTodayDate] = useState('');

    // Get parameters from URL
    const urlPage = parseInt(searchParams.get('page')) || 1
    const urlType = searchParams.get('type') || "all"
    const urlStatus = searchParams.get('status') || "all"
    const urlDepartment = searchParams.get('department') || "all"
    const urlStartDate = searchParams.get('start_date') || ""
    const urlEndDate = searchParams.get('end_date') || ""
    const urlSearch = searchParams.get('search') || ""
    const urlQuantity = parseInt(searchParams.get('quantity')) || 10

    const [page, setPage] = useState(urlPage)
    const [quantity, setQuantity] = useState(urlQuantity)
    const [totalItems, setTotalItems] = useState(0)
    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(false)
    const [exportLoading, setExportLoading] = useState(false)
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)
    const [fieldDialogOpen, setFieldDialogOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState(urlSearch)
    const [hasActiveFilters, setHasActiveFilters] = useState(false)
    const [filtersApplied, setFiltersApplied] = useState(false)
    const [filters, setFilters] = useState({
        type: urlType,
        status: urlStatus,
        department: urlDepartment,
        startDate: urlStartDate,
        endDate: urlEndDate
    })
    const [filterOptions, setFilterOptions] = useState({
        types: [],
        statuses: [],
        departments: [],
        employees: []
    })

    // Initialize with empid always selected
    const [selectedFields, setSelectedFields] = useState(
        fieldOptions.map(f => f.id)
    )

    // Set today's date on component mount
    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toLocaleDateString('en-GB'); // DD/MM/YYYY format
        setTodayDate(formattedDate);
    }, []);

    const updateURLParams = (params) => {
        const newParams = new URLSearchParams()

        // Add all non-default values to URL
        Object.entries(params).forEach(([key, value]) => {
            if (value && value !== 'all' && value !== '' && value !== 1 && value !== 10) {
                // Convert keys to URL format
                const urlKey = key === 'startDate' ? 'start_date'
                    : key === 'endDate' ? 'end_date'
                        : key
                newParams.set(urlKey, value)
            }
        })

        setSearchParams(newParams)
    }

    //Chip color for statuses
    const chipColor = (status) => {
        if (!status) return "default"
        switch (status.toLowerCase()) {
            case "pending": return "primary"
            case "accepted": return "success"
            case "rejected": return "error"
            case "cancelled": return "warning"
            default: return "default"
        }
    }

    //chip color for leave types
    const getLeaveTypeColor = (leaveType) => {
        switch (leaveType.toLowerCase()) {
            case "annual":
                return "blue"
            case "sick":
                return "green"
            case "paternity":
                return "purple"
            case "maternity":
                return "pink"
            case "emergency":
                return "red"
            default:
                return "gray"
        }
    }

    //function to capitalize first letter of string
    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
    }

    //load reports with current filters and search
    const loadReports = async () => {
        await loadReportsWithFilters(filters, searchTerm)
    }

    //toggle field selection for display
    const handleToggleField = (fieldId) => {
        // Don't allow deselection of empid
        if (fieldId === "empid") return;

        setSelectedFields(prev => {
            if (prev.includes(fieldId)) {
                return prev.filter(f => f !== fieldId);
            } else {
                return [...prev, fieldId];
            }
        });
    }

    const handleSelectAllFields = () => {
        setSelectedFields(fieldOptions.map(f => f.id))
    }

    const handleSelectNoneFields = () => {
        // Always keep empid selected
        setSelectedFields(["empid"])
    }

    //export reports based on filters and search
    const handleExport = async () => {
        setExportLoading(true)
        try {
            const formatDateForSQL = (dateString) => {
                if (!dateString) return undefined
                const [day, month, year] = dateString.split('/')
                return `${year}-${month}-${day}`
            }

            const APIData = {
                type: filters.type !== 'all' ? filters.type : undefined,
                status: filters.status !== 'all' ? filters.status : undefined,
                department: filters.department !== 'all' ? filters.department : undefined,
                start_date: filters.startDate ? formatDateForSQL(filters.startDate) : undefined,
                end_date: filters.endDate ? formatDateForSQL(filters.endDate) : undefined,
                search: searchTerm,
                fields: selectedFields.join(",") || undefined
            }

            Object.keys(APIData).forEach(key => APIData[key] === undefined && delete APIData[key])

            // Use the new CSV export function
            const result = await exportLeaveReports(APIData)

            if (result.status === "success") {
                toast.success("Report exported successfully")
            } else {
                toast.error("Failed to export report")
            }
        } catch (error) {
            console.error("Error exporting:", error)
            if (error.response && error.response.status === 401) {
                toast.error("Authentication failed. Please log in again.")
            } else if (error.response && error.response.status === 500) {
                toast.error("Server error during export. Please try again later.")
            } else {
                toast.error("Error exporting report")
            }
        }
        setExportLoading(false)
        setFieldDialogOpen(false)
    }

    //search by name and empid
    const handleSearch = (term) => {
        setSearchTerm(term)
        setPage(1)
    }

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }))
    }

    // Check if any filters are active
    const checkActiveFilters = () => {
        return (
            filters.type !== "all" ||
            filters.status !== "all" ||
            filters.department !== "all" ||
            filters.startDate !== "" ||
            filters.endDate !== "" ||
            searchTerm !== ""
        )
    }

    const applyFilters = () => {
        setPage(1)
        setFiltersApplied(true)
        loadReports()
        setFilterDialogOpen(false)
    }

    const clearFilters = () => {
        // Reset all filter states first
        const resetFilters = {
            type: "all",
            status: "all",
            department: "all",
            startDate: "",
            endDate: ""
        }

        setFilters(resetFilters)
        setSearchTerm("")
        setPage(1)
        setFiltersApplied(false)
        setSearchParams({})

        // Load reports with reset filters AFTER state updates
        setTimeout(() => {
            loadReportsWithFilters(resetFilters, "")
        }, 0)
    }

    const loadReportsWithFilters = async (filterValues, searchValue) => {
        setLoading(true)
        try {
            // Convert date format from DD/MM/YYYY to YYYY-MM-DD for SQL Server
            const formatDateForSQL = (dateString) => {
                if (!dateString) return undefined
                const [day, month, year] = dateString.split('/')
                return `${year}-${month}-${day}`
            }

            const APIData = {
                type: filterValues.type !== 'all' ? filterValues.type : undefined,
                status: filterValues.status !== 'all' ? filterValues.status : undefined,
                department: filterValues.department !== 'all' ? filterValues.department : undefined,
                start_date: filterValues.startDate ? formatDateForSQL(filterValues.startDate) : undefined,
                end_date: filterValues.endDate ? formatDateForSQL(filterValues.endDate) : undefined,
                search: searchValue,
                page: page,
                quantity: quantity
            }

            // Update URL with current filters (keep original format for display)
            updateURLParams({
                type: filterValues.type,
                status: filterValues.status,
                department: filterValues.department,
                startDate: filterValues.startDate,
                endDate: filterValues.endDate,
                search: searchValue,
                page: page,
                quantity: quantity
            })

            // Remove undefined values
            Object.keys(APIData).forEach(key => APIData[key] === undefined && delete APIData[key])

            const result = await fetchLeaveReports(APIData)

            if (result.status === "success") {
                setReports(result.data)
                setTotalItems(result.total)
                if (result.filters) {
                    setFilterOptions(result.filters)
                }
            } else {
                toast.error("Failed to fetch leave reports")
            }
        } catch (error) {
            console.error("Error loading reports:", error)
            toast.error("Error loading leave reports")
        }
        setLoading(false)
    }
    useEffect(() => {
        loadReports()
    }, [page, quantity, searchTerm])

    // Update hasActiveFilters whenever filters change
    useEffect(() => {
        setHasActiveFilters(checkActiveFilters())
    }, [filters, searchTerm])

    return (
        <>
            <Paper className="pagePaper">
                <span className="info">
                    <h5>Leave Management Reports</h5>
                    <SearchBar searchText="Search by name..." onSearch={handleSearch} searchType="name" />
                </span>

                {/* Today's Date Display */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: "30px" }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'var(--secondary-color)' }}>
                        Today's Date: {todayDate}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: "20px" }}>
                        <Button
                            variant="contained"
                            startIcon={
                                <FilterListIcon sx={{ color: "white" }} />
                            }
                            onClick={() => setFilterDialogOpen(true)}
                            sx={{
                                backgroundColor: "var(--secondary-color)",
                                position: "relative"
                            }}
                        >
                            Filters
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon sx={{ color: "white" }} />}
                            onClick={() => setFieldDialogOpen(true)}
                            sx={{ backgroundColor: "var(--secondary-color)" }}
                        >
                            Export
                        </Button>
                    </Box>
                </Box>

                {/* Active Filters Display */}
                {hasActiveFilters && filtersApplied && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "transparent",
                            borderRadius: 2,
                            border: "1px solid var(--text-secondary)",
                            transition: "all 0.3s ease-in-out",
                            animation: "fadeIn 0.3s ease-in-out"
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <FilterListIcon sx={{ fontSize: 18, color: "var(--text-primary)", mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "var(--text-primary)" }}>
                                Active Filters
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                            {filters.type !== "all" && (
                                <Chip
                                    label={`Type: ${filters.type}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onDelete={() => {
                                        const newFilters = { ...filters, type: "all" }
                                        setFilters(newFilters)
                                        setPage(1)
                                        updateURLParams({
                                            ...newFilters,
                                            search: searchTerm,
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(newFilters, searchTerm)
                                    }}
                                />
                            )}

                            {filters.status !== "all" && (
                                <Chip
                                    label={`Status: ${capitalizeFirstLetter(filters.status)}`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    onDelete={() => {
                                        const newFilters = { ...filters, status: "all" }
                                        setFilters(newFilters)
                                        setPage(1)
                                        updateURLParams({
                                            ...newFilters,
                                            search: searchTerm,
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(newFilters, searchTerm)
                                    }}
                                />
                            )}

                            {filters.department !== "all" && (
                                <Chip
                                    label={`Dept: ${filters.department}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    onDelete={() => {
                                        const newFilters = { ...filters, department: "all" }
                                        setFilters(newFilters)
                                        setPage(1)
                                        updateURLParams({
                                            ...newFilters,
                                            search: searchTerm,
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(newFilters, searchTerm)
                                    }}
                                />
                            )}

                            {filters.startDate && (
                                <Chip
                                    label={`From: ${filters.startDate}`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    onDelete={() => {
                                        const newFilters = { ...filters, startDate: "" }
                                        setFilters(newFilters)
                                        setPage(1)
                                        updateURLParams({
                                            ...newFilters,
                                            search: searchTerm,
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(newFilters, searchTerm)
                                    }}
                                />
                            )}

                            {filters.endDate && (
                                <Chip
                                    label={`To: ${filters.endDate}`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    onDelete={() => {
                                        const newFilters = { ...filters, endDate: "" }
                                        setFilters(newFilters)
                                        setPage(1)
                                        updateURLParams({
                                            ...newFilters,
                                            search: searchTerm,
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(newFilters, searchTerm)
                                    }}
                                />
                            )}

                            {searchTerm && (
                                <Chip
                                    label={`Search: "${searchTerm}"`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    onDelete={() => {
                                        setSearchTerm("")
                                        setPage(1)
                                        updateURLParams({
                                            ...filters,
                                            search: "",
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(filters, "")
                                    }}
                                />
                            )}

                            <Button
                                size="small"
                                sx={{
                                    fontSize: "0.75rem",
                                    height: "24px",
                                    ml: 1
                                }}
                                onClick={clearFilters}
                                variant="contained"
                                color="secondary"
                            >
                                Clear All
                            </Button>
                        </Box>
                    </Box>
                )}

                <div className="row" style={{
                    paddingTop: "20px",
                }}>
                    <div className="col-md-12"></div>
                    <TableContainer
                        sx={{
                            overflowY: "auto",
                            maxHeight: "500px"
                        }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            className="tableHead"
                                            key={column.id}
                                            align="center"
                                            sx={{
                                                minWidth: column.minWidth,
                                                backgroundColor: "var(--card-color)",
                                                color: "var(--primary-text-color)",
                                                fontSize: "10px",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                                            <CircularProgress size={100} style={{ color: "var(--secondary-color)" }} />
                                        </TableCell>
                                    </TableRow>
                                ) : reports.length > 0 ? (
                                    reports.map((row, index) => {
                                        // Calculate leave entitlements for this employee using ACTUAL API data including gender
                                        const leaveEntitlements = calculateLeaveEntitlements(
                                            row.dateofjoin,
                                            row.leave_balances,
                                            row.gender // Pass gender from API
                                        );

                                        return (
                                            <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                                {columns.map((column) => {
                                                    const value = row[column.id];
                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            align="center"
                                                            sx={{
                                                                fontSize: "10px",
                                                                color: "var(--primary-text-color)",
                                                            }}
                                                        >
                                                            {column.id === "id" ? (
                                                                // Calculate serial number based on pagination
                                                                totalItems - ((page - 1) * quantity + index)
                                                            ) : column.id === "type" ? (
                                                                <Box display="flex" justifyContent="flex-start" sx={{ paddingLeft: 2, width: "100%" }}>
                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                        <CircleIcon sx={{ color: getLeaveTypeColor(value), fontSize: 14 }} />
                                                                        <Typography variant="body2" align="left">
                                                                            {value}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            ) : column.id === "status" ? (
                                                                <Chip
                                                                    label={capitalizeFirstLetter(value)}
                                                                    color={chipColor(value)}
                                                                    size="small"
                                                                />
                                                            ) : column.id === "dateofjoin" ? (
                                                                // Directly display the date as it's already in DD/MM/YYYY format
                                                                row.dateofjoin ? row.dateofjoin : 'N/A'
                                                            ) : column.id === "probation_period" ? (
                                                                calculateProbationPeriod(row.dateofjoin)
                                                            ) : column.id === "annual_balance" ? (
                                                                leaveEntitlements.annual.display
                                                            ) : column.id === "sick_balance" ? (
                                                                leaveEntitlements.sick.display
                                                            ) : column.id === "maternity_balance" ? (
                                                                leaveEntitlements.maternity.display
                                                            ) : column.id === "paternity_balance" ? (
                                                                leaveEntitlements.paternity.display
                                                            ) : column.id === "emergency_balance" ? (
                                                                leaveEntitlements.emergency.display
                                                            ) : column.format ? (
                                                                column.format(value)
                                                            ) : (
                                                                value
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                                            <Typography color="var(--text-secondary)">
                                                No leave reports found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

                <PaginationTable
                    totalItems={totalItems}
                    itemsPerPage={quantity}
                    onPageChange={setPage}
                    onItemsPerPageChange={setQuantity}
                />
            </Paper>

            {/* Filter Dialog */}
            <Dialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                width="100%"
            >
                <DialogTitle>Filter Leave Reports</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small"
                                sx={{ width: '258px' }}>
                                <InputLabel>Leave Type</InputLabel>
                                <Select
                                    value={filters.type}
                                    label="Leave Type"
                                    onChange={(e) => handleFilterChange("type", e.target.value)}
                                    sx={{ color: "var(--text-secondary) !important", height: '55px' }}
                                >
                                    <MenuItem value="all">All Types</MenuItem>
                                    {filterOptions.types.map(type => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small"
                                sx={{ width: '258px' }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    label="Status"
                                    onChange={(e) => handleFilterChange("status", e.target.value)}
                                    sx={{ color: "var(--text-secondary) !important", height: '55px' }}
                                >
                                    <MenuItem value="all">All Statuses</MenuItem>
                                    {filterOptions.statuses.map(status => (
                                        <MenuItem key={status} value={status}>{capitalizeFirstLetter(status)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small"
                                sx={{ width: '258px' }}>
                                <InputLabel>Department</InputLabel>
                                <Select
                                    value={filters.department}
                                    label="Department"
                                    onChange={(e) => handleFilterChange("department", e.target.value)}
                                    sx={{ color: "var(--text-secondary) !important", height: '55px' }}
                                >
                                    <MenuItem value="all">All Departments</MenuItem>
                                    {filterOptions.departments.map(dept => (
                                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <ResponsiveDatePicker
                                fullWidth
                                size="small"
                                name="startDate"
                                label="Start Date From"
                                InputLabelProps={{ shrink: true }}
                                defValue={filters.startDate}
                                handleDate={(e) => handleFilterChange("startDate", e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                            <ResponsiveDatePicker
                                fullWidth
                                size="small"
                                name="endDate"
                                label="End Date To"
                                InputLabelProps={{ shrink: true }}
                                defValue={filters.endDate}
                                handleDate={(e) => handleFilterChange("endDate", e.target.value)} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setFilterDialogOpen(false)}
                        variant="contained"
                        sx={{ backgroundColor: "red" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={applyFilters}
                        variant="contained"
                        sx={{ backgroundColor: "var(--secondary-color)" }}
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Field Selection Dialog */}
            <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">Select Fields to Export</Typography>
                        <IconButton onClick={() => setFieldDialogOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Box mb={1} display="flex" gap={1}>
                        <Button
                            size="small"
                            onClick={handleSelectAllFields}
                            variant="contained"
                            sx={{
                                backgroundColor: "purple",
                            }}
                        >
                            Select All
                        </Button>
                        <Button
                            size="small"
                            onClick={handleSelectNoneFields}
                            variant="contained"
                            sx={{
                                backgroundColor: "red",
                            }}
                        >
                            Select None
                        </Button>
                    </Box>

                    <FormGroup>
                        <Grid container spacing={1}>
                            {fieldOptions.map((field) => (
                                <Grid item xs={6} key={field.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={selectedFields.includes(field.id)}
                                                onChange={() => handleToggleField(field.id)}
                                                disabled={field.id === "empid"}
                                                backgroundColor="var(--secondary-color)"
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: field.id === "empid" ? "var(--grey-500)" : "inherit" }}
                                                >
                                                    {field.label}
                                                </Typography>
                                                {field.id === "empid" && (
                                                    <Typography variant="caption" color="var(--grey-500)" sx={{ ml: 0.5 }}>
                                                        (Required)
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>

                    <Box mt={1}>
                        <Typography variant="body2" paddingTop="10px" color="var(--text-secondary)">
                            Selected: {selectedFields.length} of {fieldOptions.length} fields
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        // disabled={exportLoading}
                        startIcon={exportLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <DownloadIcon sx={{ color: "white" }} />}
                        sx={{
                            backgroundColor: "var(--secondary-color)",
                            '&:hover': {
                                backgroundColor: "var(--secondary-color)"
                            }
                        }}
                        size="small"
                    >
                        Export
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}